import React, { useCallback } from "react";

import cn from "classnames";

import { PopconfirmProps } from "@mapmycustomers/ui";

import useAnalytics from "@app/util/contexts/useAnalytics";

import Header from "../Header";
import Menu from "../menu";

import styles from "./SettingPage.module.scss";

interface Props {
  actions?: React.ReactNode;
  changesSaving?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  hasChanges?: boolean;
  onSaveChanges?: () => void;
  onUnDo?: () => void;
  pageContentClassName?: string;
  saveConfirmation?: PopconfirmProps;
  shouldManualClearOnLeave?: boolean;
  subTitle?: React.ReactNode;
  title: React.ReactNode;
}

const SettingPage: React.FC<Props> = ({
  actions,
  changesSaving,
  children,
  footer,
  hasChanges,
  onSaveChanges,
  onUnDo,
  pageContentClassName,
  saveConfirmation,
  shouldManualClearOnLeave,
  subTitle,
  title,
}) => {
  const analytics = useAnalytics();

  const handleSaveChanges = useCallback(() => {
    analytics.clicked(["Save Changes"]);
    onSaveChanges?.();
  }, [analytics, onSaveChanges]);

  const handleUndoChanges = useCallback(() => {
    analytics.clicked(["Undo"]);
    onUnDo?.();
  }, [analytics, onUnDo]);

  return (
    <div className={styles.container}>
      <Header
        changesSaving={changesSaving}
        hasChanges={hasChanges}
        onSaveChanges={handleSaveChanges}
        onUnDo={handleUndoChanges}
        saveConfirmation={saveConfirmation}
        shouldManualClearOnLeave={shouldManualClearOnLeave}
      />
      <div className={styles.settingsContainer}>
        <div className={styles.menuContainer}>
          <Menu />
        </div>
        <div className={styles.content}>
          <div className={styles.topWrapper}>
            <div className={styles.titleContainer}>
              <div className={styles.pageTitle} data-element-id="settings-page-title">
                {title}
              </div>
            </div>
            <div>{actions}</div>
          </div>
          {subTitle && <div className={styles.pageSubTitle}>{subTitle}</div>}
          <div className={cn(styles.pageContent, pageContentClassName)}>{children}</div>
        </div>
      </div>
      {footer && (
        <footer className={styles.footerContainer}>
          <div className={cn(styles.footer, styles.fixedWidth)}>{footer}</div>
        </footer>
      )}
    </div>
  );
};

export default SettingPage;
